import { useQuery } from "@tanstack/react-query";
import React from "react";

import { dashboardApi } from "./dashboard.api";
import { IUseDashboard } from "./dashboard.hooks.interface";
import { TDashboardPeriod, TDashboardProductType } from "./dashboard.types";

export const useDashboard = (): IUseDashboard => {
  const [productType, setProductType] =
    React.useState<TDashboardProductType>("all");
  const [period, setPeriod] = React.useState<TDashboardPeriod>(30);

  const { data: productTypesFromApi, isLoading: isLoadingProductTypes } =
    useQuery({
      queryKey: ["dashboardProductTypes"],
      queryFn: () => dashboardApi.getProductTypes(),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  const availableTypes = React.useMemo(() => {
    return productTypesFromApi ?? [];
  }, [productTypesFromApi]);

  React.useEffect(() => {
    if (availableTypes.length === 0) {
      if (productType !== "all") {
        setProductType("all");
      }
      return;
    }
    if (productType !== "all" && !availableTypes.includes(productType)) {
      setProductType("all");
    }
  }, [availableTypes, productType]);

  const { data: overviewData, isLoading: isLoadingOverview } = useQuery({
    queryKey: ["dashboardOverview", productType, period],
    queryFn: async () => {
      const params: {
        period: TDashboardPeriod;
        type_product?: TDashboardProductType;
      } = {
        period: period,
      };

      if (productType !== "all") {
        params.type_product = productType;
      }

      return dashboardApi.getOverview(params);
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    placeholderData: undefined,
  });

  const { data: productsRankingData, isLoading: isLoadingProductsRanking } =
    useQuery({
      queryKey: ["dashboardProductsRanking", productType, period],
      queryFn: async () => {
        const params: { period: TDashboardPeriod; type_product?: string } = {
          period: period,
        };

        if (productType !== "all") {
          params.type_product = productType;
        }

        return dashboardApi.getProductsRanking(params);
      },
      enabled: productType !== "all",
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      placeholderData: undefined,
    });

  const { data: plansRankingData, isLoading: isLoadingPlansRanking } = useQuery(
    {
      queryKey: ["dashboardPlansRanking", period],
      queryFn: async () => {
        return dashboardApi.getPlansRanking({ period });
      },
      enabled: productType === "all",
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      placeholderData: undefined,
    },
  );

  const [selectedYear, setSelectedYear] = React.useState<number>(
    new Date().getFullYear(),
  );

  const { data: productChartData, isLoading: isLoadingProductChart } = useQuery(
    {
      queryKey: ["dashboardProductChart", productType, selectedYear],
      queryFn: async () => {
        const params: {
          type_product?: TDashboardProductType;
          year: number;
        } = {
          year: selectedYear,
        };

        if (productType !== "all") {
          params.type_product = productType;
        }

        return dashboardApi.getChart(params);
      },
      enabled: productType !== "all",
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      placeholderData: undefined,
    },
  );

  const { data: planChartData, isLoading: isLoadingPlanChart } = useQuery({
    queryKey: ["dashboardPlanChart", selectedYear],
    queryFn: async () => {
      return dashboardApi.getPlanChart({ year: selectedYear });
    },
    enabled: productType === "all",
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    placeholderData: undefined,
  });

  const chartData = productType === "all" ? planChartData : productChartData;
  const isLoadingChart =
    productType === "all" ? isLoadingPlanChart : isLoadingProductChart;

  const isLoading =
    isLoadingOverview ||
    isLoadingProductsRanking ||
    isLoadingPlansRanking ||
    isLoadingProductTypes;

  return {
    productType,
    setProductType,
    period,
    setPeriod,
    overviewData: overviewData || null,
    isLoading,
    productsRankingData: productsRankingData || null,
    plansRankingData: plansRankingData || null,
    chartData: chartData || null,
    isLoadingChart,
    selectedYear,
    setSelectedYear,
    availableTypes,
    isLoadingProductTypes,
  };
};
